import {
  CheckCircleFilled,
  GiftOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Typography,
  message,
} from "antd";
import { useForm } from "antd/es/form/Form";
import BuyPackageModal from "components/modals/BuyPackageModal.component";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { useCreateUser } from "hooks/mutations/user";
import { getCustomerLookup } from "hooks/queries/user";
import { validateReferralCode } from "hooks/api/referral";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { translate } from "shared/languages";
import { RegisterUserForm } from "shared/types";
import { Gender, UserType } from "shared/types/enum";
import { colors } from "shared/theme/color";
import { DEFAULT_DATE_FORMAT, now } from "shared/utils/dayjs";
import { localizeRewardSummary } from "shared/utils/loyalty";
import { formatPhone } from "shared/utils/phone";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";
import { textPx } from "shared/constants/zoom";

const { Option } = Select;

// Two-step "Tambah Klien" (add client) flow for the multi-studio model:
//  1. Phone only. On continue, look the number up scoped to this studio.
//     - already an active client here  -> blocked with a message (stay).
//     - a person exists elsewhere      -> confirm their name, then add a
//       membership (server reuses the shared identity + login code).
//     - nobody                          -> show the create form (new login code).
// NewKaryawanModal mirrors this flow for employees (staff stays
// single-studio, so a phone registered as staff elsewhere is blocked there).
//
// Both post-phone steps carry an optional REFERRAL CODE field (a member of
// another studio joining here is still a valid referral). The field checks
// itself live against /v2/referral/validate (debounced, advisory — the server
// re-validates on save), and a successful create with a valid code CHAINS
// into the purchase popup, since the first purchase is what pays the rewards.
// The chain is convenience only: the attribution is state on the client, so
// closing the popup loses nothing.
type Step = "phone" | "create" | "confirm";

type ReferralCheck =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "valid"; referrerName: string; friendOffer: string | null }
  | { state: "invalid"; reason: string };

const REFERRAL_REASON_KEYS: Record<string, string> = {
  program_off: "referral.validate.reason_program_off",
  invalid_code: "referral.validate.reason_invalid_code",
  self_referral: "referral.validate.reason_self_referral",
  already_attributed: "referral.validate.reason_already_attributed",
  prior_purchase: "referral.validate.reason_prior_purchase",
  referrer_cap_reached: "referral.validate.reason_referrer_cap_reached",
};

const referralReasonText = (reason?: string) =>
  translate(
    (reason && REFERRAL_REASON_KEYS[reason]) ||
      "referral.validate.reason_invalid_code"
  );

const NewCustomerModal = ({
  defaultOpen = false,
  initialReferralCode,
}: {
  // The ?ref=<CODE> deep link from the public referral card: open the modal
  // on arrival with the code prefilled (consumed once).
  defaultOpen?: boolean;
  initialReferralCode?: string;
}) => {
  const [form] = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState<Step>("phone");
  const [pendingPhone, setPendingPhone] = useState("");
  const [existingName, setExistingName] = useState("");
  // The person was a client here and this studio deactivated them: the
  // confirm step then says it reactivates them rather than adding someone new.
  const [existingDeactivatedHere, setExistingDeactivatedHere] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [referral, setReferral] = useState<ReferralCheck>({ state: "idle" });
  // A successful create with a valid referral chains into the purchase popup
  // for the new client.
  const [chainCustomerId, setChainCustomerId] = useState<string | null>(null);
  const { mutate: createUser, isPending: isCreating } = useCreateUser();

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const checkSeqRef = useRef(0);

  // Consume the deep link once (the page clears the URL param right after).
  const consumedInitialRef = useRef(false);
  useEffect(() => {
    if (!defaultOpen || consumedInitialRef.current) return;
    consumedInitialRef.current = true;
    setIsModalOpen(true);
    if (initialReferralCode) {
      form.setFieldValue("referral_code", initialReferralCode.toUpperCase());
    }
  }, [defaultOpen, initialReferralCode, form]);

  const close = () => setIsModalOpen(false);
  const reset = () => {
    form.resetFields();
    setStep("phone");
    setPendingPhone("");
    setExistingName("");
    setExistingDeactivatedHere(false);
    setLookupLoading(false);
    setReferral({ state: "idle" });
  };

  // Debounced live check of the typed code against this studio's program +
  // the pending phone (self-referral / dedupe / prior purchase are all
  // phone-dependent). Sequenced so a stale response never overwrites a newer
  // one.
  const runReferralCheck = (code: string, phone: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = code.trim();
    if (!trimmed) {
      setReferral({ state: "idle" });
      return;
    }
    setReferral({ state: "checking" });
    const seq = ++checkSeqRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const result = await validateReferralCode({
          code: trimmed,
          phoneNumber: phone || undefined,
        });
        if (seq !== checkSeqRef.current) return;
        setReferral(
          result.valid
            ? {
                state: "valid",
                referrerName: result.referrer_name ?? "",
                friendOffer: result.friend_offer ?? null,
              }
            : { state: "invalid", reason: result.reason ?? "invalid_code" }
        );
      } catch {
        if (seq !== checkSeqRef.current) return;
        setReferral({ state: "invalid", reason: "invalid_code" });
      }
    }, 400);
  };

  // Step 1 -> classify the phone and branch.
  const onContinue = () => {
    form
      .validateFields(["phone_number"])
      .then(async ({ phone_number }) => {
        setLookupLoading(true);
        try {
          const result = await getCustomerLookup(phone_number);
          if (result.status === "active_member") {
            message.error(translate("messages.error.customer_already_member"));
            return;
          }
          setPendingPhone(phone_number);
          // Re-run a check the admin may have typed before the phone was set.
          const typedCode = form.getFieldValue("referral_code") ?? "";
          if (typedCode) runReferralCheck(typedCode, phone_number);
          if (result.status === "known") {
            setExistingName(result.full_name ?? "");
            setExistingDeactivatedHere(!!result.deactivated_here);
            setStep("confirm");
          } else {
            setStep("create");
          }
        } catch {
          message.error(translate("messages.error.general"));
        } finally {
          setLookupLoading(false);
        }
      })
      .catch(() => {
        // AntD already paints the inline phone error.
      });
  };

  // Shared post-create handling: read the attribution outcome the server
  // rode back beside the user, then chain into the purchase popup when a
  // referral actually attached.
  const onCreated = (response: {
    data?: { data?: { id?: string; referral_attribution?: { created: boolean; reason?: string } } };
  }) => {
    const created = response.data?.data;
    const attribution = created?.referral_attribution;
    close();
    if (attribution && !attribution.created) {
      // Registration succeeded; only the attribution was refused (usually a
      // lost race). The admin hears why instead of silently losing it.
      message.warning(
        `${translate("referral.toast.attribution_failed")} ${referralReasonText(
          attribution.reason
        )}`
      );
      return;
    }
    if (attribution?.created && created?.id) {
      setChainCustomerId(created.id);
    }
  };

  // Step 2a -> create a brand-new client.
  const onCreate = () => {
    form
      .validateFields()
      .then((values) => {
        createUser(
          {
            ...values,
            phone_number: pendingPhone,
            user_type: UserType.Customer,
          } as RegisterUserForm,
          { onSuccess: onCreated }
        );
      })
      .catch(() => {
        // AntD already paints inline errors.
      });
  };

  // Step 2b -> add an existing person as a member of this studio. No name
  // fields are sent — the server reuses the stored identity's existing name
  // untouched (see createUserByBusinessId's "person already exists" branch).
  // The referral code IS sent (this payload is hand-built, unlike onCreate's
  // values spread).
  const onConfirmAdd = () => {
    const referralCode = (form.getFieldValue("referral_code") ?? "").trim();
    createUser(
      {
        phone_number: pendingPhone,
        user_type: UserType.Customer,
        ...(referralCode ? { referral_code: referralCode } : {}),
      } as RegisterUserForm,
      { onSuccess: onCreated }
    );
  };

  const footer = (() => {
    if (step === "phone") {
      return (
        <Flex justify="end" gap={10}>
          <Button onClick={close}>{translate("button.cancel")}</Button>
          <Button type="primary" loading={lookupLoading} onClick={onContinue}>
            {translate("button.continue")}
          </Button>
        </Flex>
      );
    }
    if (step === "create") {
      return (
        <Flex justify="end" gap={10}>
          <Button onClick={() => setStep("phone")}>
            {translate("button.back")}
          </Button>
          <Button type="primary" loading={isCreating} onClick={onCreate}>
            {translate("button.add")}
          </Button>
        </Flex>
      );
    }
    return (
      <Flex justify="end" gap={10}>
        <Button onClick={() => setStep("phone")}>
          {translate("button.change_number")}
        </Button>
        <Button type="primary" loading={isCreating} onClick={onConfirmAdd}>
          {translate("button.confirm_add")}
        </Button>
      </Flex>
    );
  })();

  // Inline outcome under the referral field: a tinted card naming the
  // referrer (+ the friend-side offer) on a hit — the two facts the admin
  // reads back to the client, so they get card weight rather than caption
  // text — and the specific refusal reason otherwise.
  const cardLabelStyle: CSSProperties = {
    fontSize: textPx(11),
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: colors.ink2,
    lineHeight: 1.4,
  };
  const cardIconCircle = (color: string): CSSProperties => ({
    width: 28,
    height: 28,
    borderRadius: 999,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#FFFFFF",
    color,
    fontSize: textPx(14),
  });
  const referralFeedback = (() => {
    if (referral.state === "checking") {
      return (
        <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
          {translate("referral.validate.checking")}
        </Typography.Text>
      );
    }
    if (referral.state === "valid") {
      return (
        <div
          role="status"
          style={{
            marginTop: 6,
            borderRadius: 10,
            // Success identity on the warm palette: tinted fill + a visible
            // (not shadowed) border, per the design system.
            border: "1px solid rgba(46, 139, 94, 0.28)",
            background: colors.successTint,
          }}
        >
          <Flex align="center" gap={10} style={{ padding: "10px 12px" }}>
            <span style={cardIconCircle(colors.success)} aria-hidden>
              <CheckCircleFilled />
            </span>
            <div>
              <div style={cardLabelStyle}>
                {translate("referral.validate.referred_by")}
              </div>
              <Typography.Text
                strong
                style={{ fontSize: textPx(14), color: colors.ink }}
              >
                {referral.referrerName}
              </Typography.Text>
            </div>
          </Flex>
          {referral.friendOffer && (
            <Flex
              align="center"
              gap={10}
              style={{
                padding: "10px 12px",
                borderTop: `1px solid ${colors.line}`,
              }}
            >
              <span style={cardIconCircle(colors.pink)} aria-hidden>
                <GiftOutlined />
              </span>
              <div>
                <div style={cardLabelStyle}>
                  {translate("referral.validate.friend_offer")}
                </div>
                <Typography.Text
                  strong
                  style={{ fontSize: textPx(14), color: colors.ink }}
                >
                  {localizeRewardSummary(referral.friendOffer)}
                </Typography.Text>
              </div>
            </Flex>
          )}
        </div>
      );
    }
    if (referral.state === "invalid") {
      return (
        <Typography.Text type="danger" style={{ fontSize: textPx(13) }}>
          {referralReasonText(referral.reason)}
        </Typography.Text>
      );
    }
    return null;
  })();

  const referralField = (
    <Form.Item
      name="referral_code"
      label={translate("referral.field.code")}
      extra={referralFeedback}
      normalize={(v?: string) => v?.toUpperCase()}
    >
      <Input
        placeholder={translate("referral.field.code_placeholder")}
        onChange={(e) => runReferralCheck(e.target.value, pendingPhone)}
        allowClear
      />
    </Form.Item>
  );

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        type="primary"
        icon={<PlusOutlined />}
      >
        {translate("button.add")} {translate(UserType.Customer)}
      </Button>
      <Modal
        title={translate("title.create_customer")}
        open={isModalOpen}
        onCancel={close}
        afterClose={reset}
        footer={
          <ConfigProvider theme={modalButtonsTheme}>{footer}</ConfigProvider>
        }
      >
        <Form form={form} layout="vertical">
          {/* Phone is always present; in later steps it's locked context. */}
          <Form.Item
            name="phone_number"
            label={translate("label.phone_number")}
            rules={phoneRules()}
          >
            <PhoneInput
              disabled={step !== "phone"}
              placeholder={`${translate("form.placeholders.enter")} ${translate(
                "form.placeholders.phone_number"
              )}`}
            />
          </Form.Item>

          {step === "create" && (
            <>
              <Form.Item
                name="first_name"
                label={translate("label.first_name")}
                rules={[
                  {
                    required: true,
                    message: `${translate("form.error.input")} ${translate(
                      "label.first_name"
                    )} ${translate("form.error.input_end")}`,
                  },
                ]}
              >
                <Input
                  placeholder={`${translate(
                    "form.placeholders.enter"
                  )} ${translate("label.first_name")}`}
                />
              </Form.Item>

              <Form.Item name="middle_name" label={translate("label.middle_name")}>
                <Input
                  placeholder={`${translate(
                    "form.placeholders.enter"
                  )} ${translate("label.middle_name")}`}
                />
              </Form.Item>

              <Form.Item name="last_name" label={translate("label.last_name")}>
                <Input
                  placeholder={`${translate(
                    "form.placeholders.enter"
                  )} ${translate("label.last_name")}`}
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: "email", message: translate("form.error.email") },
                ]}
              >
                <Input
                  placeholder={`${translate("form.placeholders.enter")} email`}
                />
              </Form.Item>

              <Form.Item name="gender" label={translate("label.gender")}>
                <Select
                  placeholder={`${translate(
                    "form.placeholders.select"
                  )} ${translate("label.gender")}`}
                >
                  {Object.entries(Gender).map(([key, value]) => (
                    <Option key={key} value={value}>
                      {translate(`form.select.gender.${value}`)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="dob" label={translate("label.dob")}>
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder={`${translate("form.placeholders.enter")} ${translate(
                    "label.dob"
                  )}`}
                  format={DEFAULT_DATE_FORMAT}
                  disabledDate={(current) =>
                    current && current > now().endOf("day")
                  }
                />
              </Form.Item>

              {referralField}
            </>
          )}

          {step === "confirm" && referralField}
        </Form>

        {step === "confirm" && (
          <div>
            <Typography.Paragraph>
              {translate("messages.info.existing_customer_found")}
            </Typography.Paragraph>
            {existingDeactivatedHere && (
              <Typography.Paragraph type="warning">
                {translate("messages.info.existing_customer_deactivated_here")}
              </Typography.Paragraph>
            )}
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={translate("label.name")}>
                {existingName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={translate("label.phone_number")}>
                {formatPhone(pendingPhone)}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* The referral chain: straight into the first purchase, where the
          rewards actually trigger. Rendered only while chaining so the
          referral query inside starts fresh per client. The whole catalog is
          offered (includeMemberships) — a first purchase can be either kind,
          and the intro discount's scope narrows the DISCOUNT, never what the
          client is allowed to buy. */}
      {chainCustomerId && (
        <ConfigProvider theme={modalButtonsTheme}>
          <BuyPackageModal
            customerId={chainCustomerId}
            open
            includeMemberships
            onCancel={() => setChainCustomerId(null)}
          />
        </ConfigProvider>
      )}
    </>
  );
};

export default NewCustomerModal;
